import { useEffect, useRef, useState } from 'react'
import { KeyRound, LogOut } from 'lucide-react'
import {
  GOLD,
  GOLD_DIM,
  GREY_200,
  GREY_300,
  GREY_400,
  GREY_500,
  WARNING,
  WHITE,
  glassCardStyle,
} from '@/lib/genesis'

// Bottom-of-sidebar user-menu pattern (Claude-UI style), shared shape
// across the operator console and customer portal shells (operator
// feedback: "the bottom pane of the left nav should be congruent between
// the three UI services"). The ui/ app (ui/src/components/layout/
// RootLayout.tsx) does NOT have this exact pattern today -- its footer
// shows a static email/name block plus a plain Logout button, with no
// click-to-open popover and no role information at all. This component is
// therefore a NEW pattern for the two apps-future shells, not a port of an
// existing ui/ component; porting it back to ui/ is a decision left to the
// operator (ui/ is out of scope for this pass).
//
// Chip: circular initials avatar (gold-dim bg, gold initials) + display
// name (email fallback), truncated. Click toggles an upward glass-card
// popover containing: full email, role line (role lives HERE now, not on
// the chip), Change password, Sign out.

export interface UserMenuProps {
  email: string
  name?: string | null
  roleLabel: string
  onChangePassword?: () => void
  onSignOut: () => void
}

function initialsFor(name: string | null | undefined, email: string): string {
  const source = (name && name.trim()) || email
  const parts = source
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export function UserMenu({ email, name, roleLabel, onChangePassword, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handlePointerDown(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const displayName = (name && name.trim()) || email
  const initials = initialsFor(name, email)

  return (
    <div ref={containerRef} className="relative">
      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute bottom-full left-0 mb-2 w-full min-w-[220px] p-2"
          style={glassCardStyle}
        >
          <div className="mb-2 border-b px-2 pb-2" style={{ borderColor: GREY_500 }}>
            <p className="truncate text-xs" style={{ color: WHITE }} title={email}>
              {email}
            </p>
            <p className="mt-1 text-[11px] uppercase tracking-wide" style={{ color: GREY_400 }}>
              Role: <span className="font-mono" style={{ color: GREY_300 }}>{roleLabel}</span>
            </p>
          </div>
          {onChangePassword && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onChangePassword()
              }}
              className="glass-button flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors"
              style={{ color: GREY_200 }}
            >
              <KeyRound className="h-4 w-4" style={{ color: GREY_400 }} aria-hidden="true" />
              Change password
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
            className="glass-button flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors"
            style={{ color: WARNING }}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${displayName}`}
        className="glass-button flex w-full items-center gap-2 rounded-md p-2 text-left transition-colors"
      >
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          style={{ backgroundColor: GOLD_DIM, color: GOLD }}
          aria-hidden="true"
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm" style={{ color: WHITE }} title={displayName}>
          {displayName}
        </span>
      </button>
    </div>
  )
}
